import { z } from 'zod';
import { APPOINTMENT_EVENT_OUTCOMES, type AppointmentEventOutcome } from './appointment-outcome.js';

// Setter spec §03.1, §03.2, §15.3, §15.7 (C1): contact frequency policy per project and the appointment-outcome event
// the recovery cadences subscribe to. Browser-safe.

// ───────────── Frequency policy (per project, spec defaults) ─────────────

/**
 * Absolute safety ceilings: no project setting can exceed them (owner decision "spec wins, configurable" keeps a hard
 * cap so a misconfiguration never turns into harassment). The spec cadences fit inside: form = 2 calls on day 1 and
 * 5 calls + 2 WhatsApp in 7 days; no-show = 3 calls + 1 WhatsApp in 4 days.
 */
export const FREQUENCY_CEILINGS = {
  maxCallsPerDay: 3,
  maxCallsPer7Days: 7,
  /** Lower bound: calls are never closer than this. */
  minMinutesBetweenCalls: 60,
  maxWhatsappPer24h: 2,
  maxWhatsappPer7Days: 4,
} as const;

/** Spec defaults (§03.1 / §15.3): day 1 has two calls "unas horas" apart; 5 calls and 2 WhatsApp in 7 days. */
export const FREQUENCY_DEFAULTS = {
  maxCallsPerDay: 2,
  maxCallsPer7Days: 5,
  minMinutesBetweenCalls: 180,
  maxWhatsappPer24h: 1,
  maxWhatsappPer7Days: 2,
} as const;

export const FrequencyPolicySchema = z.object({
  /** Unanswered outbound calls per local calendar day (contact-wide). */
  maxCallsPerDay: z.number().int().min(1).max(FREQUENCY_CEILINGS.maxCallsPerDay),
  /** Unanswered outbound calls in a rolling 7 days (contact-wide). */
  maxCallsPer7Days: z.number().int().min(1).max(FREQUENCY_CEILINGS.maxCallsPer7Days),
  /** Minimum gap between two outbound calls. */
  minMinutesBetweenCalls: z.number().int().min(FREQUENCY_CEILINGS.minMinutesBetweenCalls).max(24 * 60),
  /** Automated WhatsApp follow-ups without a reply in a rolling 24 h. */
  maxWhatsappPer24h: z.number().int().min(1).max(FREQUENCY_CEILINGS.maxWhatsappPer24h),
  /** Automated WhatsApp follow-ups without a reply in a rolling 7 days. */
  maxWhatsappPer7Days: z.number().int().min(1).max(FREQUENCY_CEILINGS.maxWhatsappPer7Days),
});
export type FrequencyPolicy = z.infer<typeof FrequencyPolicySchema>;

/** Stored value: any subset (missing keys fall back to the spec defaults). */
export const FrequencyPolicyInputSchema = FrequencyPolicySchema.partial();
export type FrequencyPolicyInput = z.infer<typeof FrequencyPolicyInputSchema>;

const clamp = (v: unknown, min: number, max: number, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : fallback;

/** Effective policy: stored overrides over the spec defaults, always clamped to the safety ceilings. */
export function resolveFrequencyPolicy(stored: unknown): FrequencyPolicy {
  const s = (stored && typeof stored === 'object' ? stored : {}) as Record<string, unknown>;
  const C = FREQUENCY_CEILINGS;
  const D = FREQUENCY_DEFAULTS;
  return {
    maxCallsPerDay: clamp(s['maxCallsPerDay'], 1, C.maxCallsPerDay, D.maxCallsPerDay),
    maxCallsPer7Days: clamp(s['maxCallsPer7Days'], 1, C.maxCallsPer7Days, D.maxCallsPer7Days),
    minMinutesBetweenCalls: clamp(s['minMinutesBetweenCalls'], C.minMinutesBetweenCalls, 24 * 60, D.minMinutesBetweenCalls),
    maxWhatsappPer24h: clamp(s['maxWhatsappPer24h'], 1, C.maxWhatsappPer24h, D.maxWhatsappPer24h),
    maxWhatsappPer7Days: clamp(s['maxWhatsappPer7Days'], 1, C.maxWhatsappPer7Days, D.maxWhatsappPer7Days),
  };
}

export const FREQUENCY_POLICY_LABELS: Record<keyof FrequencyPolicy, { label: string; help: string }> = {
  maxCallsPerDay: { label: 'Llamadas sin respuesta por día', help: 'Por contacto, en su día local. La cadencia de formulario usa 2 el primer día.' },
  maxCallsPer7Days: { label: 'Llamadas sin respuesta en 7 días', help: 'Por contacto, en todos los proyectos. La cadencia de formulario usa hasta 5.' },
  minMinutesBetweenCalls: { label: 'Minutos mínimos entre llamadas', help: 'Separación entre dos llamadas al mismo contacto.' },
  maxWhatsappPer24h: { label: 'WhatsApp de seguimiento sin respuesta en 24 h', help: 'Mensajes automáticos que el prospecto no ha contestado.' },
  maxWhatsappPer7Days: { label: 'WhatsApp de seguimiento sin respuesta en 7 días', help: 'La cadencia de formulario usa hasta 2.' },
};

// ───────────── Appointment outcome event (Wave 2A emits, recovery cadences subscribe) ─────────────

export const APPOINTMENT_OUTCOME_EVENT = 'appointment.outcome_recorded' as const;
/** Outcome vocabulary of the event (defined by the booking loop, `appointment-outcome.ts`): `held` = Realizada. */
export const RECORDED_APPOINTMENT_OUTCOMES = APPOINTMENT_EVENT_OUTCOMES;
export const RecordedAppointmentOutcomeSchema = z.enum(APPOINTMENT_EVENT_OUTCOMES);
export type RecordedAppointmentOutcome = AppointmentEventOutcome;
export const RECORDED_APPOINTMENT_OUTCOME_LABELS: Record<RecordedAppointmentOutcome, string> = {
  held: 'Cita realizada',
  no_show: 'No show (confirmado)',
  cancelled: 'Cita cancelada',
  rescheduled: 'Cita reprogramada',
};
